import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {IAppError} from "@pg-mono/request-state";

import {ITrackAmplitudeOpenGalleryParams, trackAmplitudeOpenGallery} from "../../../../offer/tracking/amplitude/track_amplitude_open_gallery";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {getAmplitudeViewItemEventProperties} from "../../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeComponentAction} from "../../../../tracking/amplitude/track_amplitude_component_action";
import {getAmplitudeInquirySource} from "../../../tracking/amplitude/get_amplitude_inquiry_source";
import {getAmplitudeLeadType} from "../../../tracking/amplitude/get_amplitude_lead_type";
import {
    trackAmplitudeLeadFormClose,
    trackAmplitudeLeadFormError,
    trackAmplitudeLeadFormGenerateLead,
    trackAmplitudeLeadFormStart
} from "../../../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationData} from "../../../types/IApplicationData";
import {IAmplitudeLeadFormTrackingContext} from "../../../types/lead/IAmplitudeLeadFormEventTracking";
import {TinderMultiLeadProperty} from "../../../types/tinder_multi_lead/TinderMultiLeadProperty";
import {ApplicationSource, ApplicationSourceSection} from "../../../utils/form/application_form_source";
import {getTinderMultiLeadLeadData, getTinderMultiLeadViewItemOffer, getTinderMultiLeadViewItemProperty} from "./get_tinder_multi_lead_lead_data";

/** Shared `location` / `form_location` for every Step-3 ("Przejrzyj i zapytaj") event. */
export const TINDER_MULTI_LEAD_LOCATION = "rp_multilead_review";
/**
 * `form_location` for opening the deck itself. The deck takes the classic multilead's place in the
 * post-lead flow, so it reports the same location as `trackClassicMultiLeadStart` — which keeps the
 * deck-open start distinguishable from the per-card survey start (`rp_multilead_review`).
 */
const TINDER_MULTI_LEAD_DECK_LOCATION = "rp_multilead_post_lead";
/** Fallback `cta_text` when the deck was opened without a click (post price-reveal lead flow). */
const TINDER_MULTI_LEAD_DECK_CTA_TEXT = "ZAPYTAJ";
const TINDER_MULTI_LEAD_BUTTON_CATEGORY = "rp_multilead";
const TINDER_MULTI_LEAD_SOURCE = ApplicationSource.RecommendedProperty;
const TINDER_MULTI_LEAD_SOURCE_SECTION = ApplicationSourceSection.MULTILEAD;

type TinderMultiLeadAdditionalQuestion = {value: string; label: string};

const getIds = (property: TinderMultiLeadProperty) => ({itemId: property.offer.id, propertyId: property.id});

export const trackTinderMultiLeadViewItem = ({property, index}: {property: IPropertyDetail; index: number}) => {
    const baseProperties = getAmplitudeViewItemEventProperties({
        offer: getTinderMultiLeadViewItemOffer(property),
        property: getTinderMultiLeadViewItemProperty(property)
    });

    trackAmplitudeEvent("view_item", {
        ...baseProperties,
        index,
        location: TINDER_MULTI_LEAD_LOCATION,
        preference_match_percent: null
    });
};

export const trackTinderMultiLeadOpenGallery = ({
    property,
    galleryView,
    photoSection,
    mediaTabNavigation,
    photosTotal,
    photoIndex
}: {
    property: TinderMultiLeadProperty;
    galleryView: ITrackAmplitudeOpenGalleryParams["galleryView"];
    photoSection: ITrackAmplitudeOpenGalleryParams["photoSection"];
    mediaTabNavigation?: ITrackAmplitudeOpenGalleryParams["mediaTabNavigation"];
    photosTotal?: number;
    photoIndex?: number;
}) => {
    const {itemId, propertyId} = getIds(property);

    trackAmplitudeOpenGallery({
        location: TINDER_MULTI_LEAD_LOCATION,
        offerId: itemId,
        propertyId,
        galleryView,
        photoSection,
        mediaTabNavigation,
        photosTotal,
        photoIndex
    });
};

export const trackTinderMultiLeadMediaButton = ({property, kind}: {property: TinderMultiLeadProperty; kind: "plan" | "gallery"}) => {
    const {itemId, propertyId} = getIds(property);
    const isPlan = kind === "plan";

    trackAmplitudeEvent("button_click", {
        button_name: isPlan ? "view_floor_plan" : "view_gallery",
        value: isPlan ? "see_floor_plan" : "see_gallery",
        button_link: null,
        button_category: TINDER_MULTI_LEAD_BUTTON_CATEGORY,
        item_id: itemId,
        property_id: propertyId
    });
};

export const trackTinderMultiLeadRate = ({property, value}: {property: TinderMultiLeadProperty; value: "like" | "dislike"}) => {
    const {itemId, propertyId} = getIds(property);

    trackAmplitudeEvent("button_click", {
        button_name: "rp_recommendations_rate",
        value,
        button_link: null,
        button_category: TINDER_MULTI_LEAD_BUTTON_CATEGORY,
        item_id: itemId,
        property_id: propertyId
    });
};

export const trackTinderMultiLeadMapInteraction = ({property}: {property: TinderMultiLeadProperty}) => {
    const {itemId, propertyId} = getIds(property);

    trackAmplitudeComponentAction({
        componentName: "investment_location_map",
        action: "first_interaction",
        location: TINDER_MULTI_LEAD_LOCATION,
        itemId,
        propertyId
    });
};

const getTrackingContext = (property: TinderMultiLeadProperty, ctaText: string, formLocation: string): IAmplitudeLeadFormTrackingContext => {
    const {itemId, propertyId} = getIds(property);

    return {
        inquiry_source: getAmplitudeInquirySource(TINDER_MULTI_LEAD_SOURCE),
        cta_text: ctaText,
        form_location: formLocation,
        source_id: TINDER_MULTI_LEAD_SOURCE,
        lead_type: getAmplitudeLeadType(TINDER_MULTI_LEAD_SOURCE_SECTION),
        item_id: itemId,
        property_id: propertyId
    };
};

const getSurveyTrackingContext = (property: TinderMultiLeadProperty, ctaText: string): IAmplitudeLeadFormTrackingContext =>
    getTrackingContext(property, ctaText, TINDER_MULTI_LEAD_LOCATION);

/**
 * Deck ("Przejrzyj i zapytaj") displayed with a loaded queue — the multilead form is on screen, so
 * this mirrors `trackClassicMultiLeadStart`: one event per modal session, all queued offers in
 * `PRODUCTS`. The per-card survey start (DSA-A-79) stays on top of this.
 */
export const trackTinderMultiLeadDeckStart = ({queue, ctaText}: {queue: TinderMultiLeadProperty[]; ctaText?: string}) => {
    const [firstProperty] = queue;

    if (!firstProperty) {
        return;
    }

    trackAmplitudeLeadFormStart({
        trackingContext: getTrackingContext(firstProperty, ctaText || TINDER_MULTI_LEAD_DECK_CTA_TEXT, TINDER_MULTI_LEAD_DECK_LOCATION),
        leadData: queue.map(getTinderMultiLeadLeadData),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill"
    });
};

export const trackTinderMultiLeadSurveyStart = ({property}: {property: TinderMultiLeadProperty}) => {
    trackAmplitudeLeadFormStart({
        trackingContext: getSurveyTrackingContext(property, "ZAPYTAJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill"
    });
};

export const trackTinderMultiLeadSurveyClose = ({property, selectedQuestionValues}: {property: TinderMultiLeadProperty; selectedQuestionValues: string[]}) => {
    trackAmplitudeLeadFormClose({
        trackingContext: getSurveyTrackingContext(property, "ZAPYTAJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill",
        formSnapshot: selectedQuestionValues.length ? {questions: selectedQuestionValues} : undefined
    });
};

export const trackTinderMultiLeadSurveyError = ({
    property,
    appError,
    errorCode
}: {
    property: TinderMultiLeadProperty;
    appError: IAppError;
    errorCode?: string;
}) => {
    trackAmplitudeLeadFormError({
        trackingContext: getSurveyTrackingContext(property, "WYŚLIJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        appError,
        ...(errorCode ? {errorCode} : {})
    });
};

export const trackTinderMultiLeadSurveyGenerate = ({
    property,
    application,
    additionalQuestions
}: {
    property: TinderMultiLeadProperty;
    application: IApplicationData;
    additionalQuestions: TinderMultiLeadAdditionalQuestion[];
}) => {
    trackAmplitudeLeadFormGenerateLead({
        trackingContext: getSurveyTrackingContext(property, "WYŚLIJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill",
        leadResponse: application,
        inquirySelection: {additionalQuestions}
    });
};
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {IAppError} from "@pg-mono/request-state";

import {ITrackAmplitudeOpenGalleryParams, trackAmplitudeOpenGallery} from "../../../../offer/tracking/amplitude/track_amplitude_open_gallery";
import {IPropertyDetail} from "../../../../property/types/IPropertyDetail";
import {getAmplitudeViewItemEventProperties} from "../../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeComponentAction} from "../../../../tracking/amplitude/track_amplitude_component_action";
import {getAmplitudeInquirySource} from "../../../tracking/amplitude/get_amplitude_inquiry_source";
import {getAmplitudeLeadType} from "../../../tracking/amplitude/get_amplitude_lead_type";
import {
    trackAmplitudeLeadFormClose,
    trackAmplitudeLeadFormError,
    trackAmplitudeLeadFormGenerateLead,
    trackAmplitudeLeadFormStart
} from "../../../tracking/amplitude/track_amplitude_lead_form_events";
import {IApplicationData} from "../../../types/IApplicationData";
import {IAmplitudeLeadFormTrackingContext} from "../../../types/lead/IAmplitudeLeadFormEventTracking";
import {TinderMultiLeadProperty} from "../../../types/tinder_multi_lead/TinderMultiLeadProperty";
import {ApplicationSource, ApplicationSourceSection} from "../../../utils/form/application_form_source";
import {getTinderMultiLeadLeadData, getTinderMultiLeadViewItemOffer, getTinderMultiLeadViewItemProperty} from "./get_tinder_multi_lead_lead_data";

/** Shared `location` / `form_location` for every Step-3 ("Przejrzyj i zapytaj") event. */
export const TINDER_MULTI_LEAD_LOCATION = "rp_multilead_review";
/**
 * `form_location` for opening the deck itself. The deck takes the classic multilead's place in the
 * post-lead flow, so it reports the same location as `trackClassicMultiLeadStart` — which keeps the
 * deck-open start distinguishable from the per-card survey start (`rp_multilead_review`).
 */
const TINDER_MULTI_LEAD_DECK_LOCATION = "rp_multilead_post_lead";
/** Fallback `cta_text` when the deck was opened without a click (post price-reveal lead flow). */
const TINDER_MULTI_LEAD_DECK_CTA_TEXT = "ZAPYTAJ";
const TINDER_MULTI_LEAD_BUTTON_CATEGORY = "rp_multilead";
const TINDER_MULTI_LEAD_SOURCE = ApplicationSource.RecommendedProperty;
const TINDER_MULTI_LEAD_SOURCE_SECTION = ApplicationSourceSection.MULTILEAD;

type TinderMultiLeadAdditionalQuestion = {value: string; label: string};

const getIds = (property: TinderMultiLeadProperty) => ({itemId: property.offer.id, propertyId: property.id});

export const trackTinderMultiLeadViewItem = ({property, index}: {property: IPropertyDetail; index: number}) => {
    const baseProperties = getAmplitudeViewItemEventProperties({
        offer: getTinderMultiLeadViewItemOffer(property),
        property: getTinderMultiLeadViewItemProperty(property)
    });

    trackAmplitudeEvent("view_item", {
        ...baseProperties,
        index,
        location: TINDER_MULTI_LEAD_LOCATION,
        preference_match_percent: null
    });
};

export const trackTinderMultiLeadOpenGallery = ({
    property,
    galleryView,
    photoSection,
    mediaTabNavigation,
    photosTotal,
    photoIndex
}: {
    property: TinderMultiLeadProperty;
    galleryView: ITrackAmplitudeOpenGalleryParams["galleryView"];
    photoSection: ITrackAmplitudeOpenGalleryParams["photoSection"];
    mediaTabNavigation?: ITrackAmplitudeOpenGalleryParams["mediaTabNavigation"];
    photosTotal?: number;
    photoIndex?: number;
}) => {
    const {itemId, propertyId} = getIds(property);

    trackAmplitudeOpenGallery({
        location: TINDER_MULTI_LEAD_LOCATION,
        offerId: itemId,
        propertyId,
        galleryView,
        photoSection,
        mediaTabNavigation,
        photosTotal,
        photoIndex
    });
};

export const trackTinderMultiLeadMediaButton = ({property, kind}: {property: TinderMultiLeadProperty; kind: "plan" | "gallery"}) => {
    const {itemId, propertyId} = getIds(property);
    const isPlan = kind === "plan";

    trackAmplitudeEvent("button_click", {
        button_name: isPlan ? "view_floor_plan" : "view_gallery",
        value: isPlan ? "see_floor_plan" : "see_gallery",
        button_link: null,
        button_category: TINDER_MULTI_LEAD_BUTTON_CATEGORY,
        item_id: itemId,
        property_id: propertyId
    });
};

export const trackTinderMultiLeadRate = ({property, value}: {property: TinderMultiLeadProperty; value: "like" | "dislike"}) => {
    const {itemId, propertyId} = getIds(property);

    trackAmplitudeEvent("button_click", {
        button_name: "rp_recommendations_rate",
        value,
        button_link: null,
        button_category: TINDER_MULTI_LEAD_BUTTON_CATEGORY,
        item_id: itemId,
        property_id: propertyId
    });
};

export const trackTinderMultiLeadMapInteraction = ({property}: {property: TinderMultiLeadProperty}) => {
    const {itemId, propertyId} = getIds(property);

    trackAmplitudeComponentAction({
        componentName: "investment_location_map",
        action: "first_interaction",
        location: TINDER_MULTI_LEAD_LOCATION,
        itemId,
        propertyId
    });
};

const getTrackingContext = (property: TinderMultiLeadProperty, ctaText: string, formLocation: string): IAmplitudeLeadFormTrackingContext => {
    const {itemId, propertyId} = getIds(property);

    return {
        inquiry_source: getAmplitudeInquirySource(TINDER_MULTI_LEAD_SOURCE),
        cta_text: ctaText,
        form_location: formLocation,
        source_id: TINDER_MULTI_LEAD_SOURCE,
        lead_type: getAmplitudeLeadType(TINDER_MULTI_LEAD_SOURCE_SECTION),
        item_id: itemId,
        property_id: propertyId
    };
};

const getSurveyTrackingContext = (property: TinderMultiLeadProperty, ctaText: string): IAmplitudeLeadFormTrackingContext =>
    getTrackingContext(property, ctaText, TINDER_MULTI_LEAD_LOCATION);

/**
 * Deck ("Przejrzyj i zapytaj") displayed with a loaded queue — the multilead form is on screen, so
 * this mirrors `trackClassicMultiLeadStart`: one event per modal session, all queued offers in
 * `PRODUCTS`. The per-card survey start (DSA-A-79) stays on top of this.
 */
export const trackTinderMultiLeadDeckStart = ({queue, ctaText}: {queue: TinderMultiLeadProperty[]; ctaText?: string}) => {
    const [firstProperty] = queue;

    if (!firstProperty) {
        return;
    }

    trackAmplitudeLeadFormStart({
        trackingContext: getTrackingContext(firstProperty, ctaText || TINDER_MULTI_LEAD_DECK_CTA_TEXT, TINDER_MULTI_LEAD_DECK_LOCATION),
        leadData: queue.map(getTinderMultiLeadLeadData),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill"
    });
};

export const trackTinderMultiLeadSurveyStart = ({property}: {property: TinderMultiLeadProperty}) => {
    trackAmplitudeLeadFormStart({
        trackingContext: getSurveyTrackingContext(property, "ZAPYTAJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill"
    });
};

export const trackTinderMultiLeadSurveyClose = ({property, selectedQuestionValues}: {property: TinderMultiLeadProperty; selectedQuestionValues: string[]}) => {
    trackAmplitudeLeadFormClose({
        trackingContext: getSurveyTrackingContext(property, "ZAPYTAJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill",
        formSnapshot: selectedQuestionValues.length ? {questions: selectedQuestionValues} : undefined
    });
};

export const trackTinderMultiLeadSurveyError = ({
    property,
    appError,
    errorCode
}: {
    property: TinderMultiLeadProperty;
    appError: IAppError;
    errorCode?: string;
}) => {
    trackAmplitudeLeadFormError({
        trackingContext: getSurveyTrackingContext(property, "WYŚLIJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        appError,
        ...(errorCode ? {errorCode} : {})
    });
};

export const trackTinderMultiLeadSurveyGenerate = ({
    property,
    application,
    additionalQuestions
}: {
    property: TinderMultiLeadProperty;
    application: IApplicationData;
    additionalQuestions: TinderMultiLeadAdditionalQuestion[];
}) => {
    trackAmplitudeLeadFormGenerateLead({
        trackingContext: getSurveyTrackingContext(property, "WYŚLIJ"),
        leadData: getTinderMultiLeadLeadData(property),
        source: TINDER_MULTI_LEAD_SOURCE,
        sourceSection: TINDER_MULTI_LEAD_SOURCE_SECTION,
        eventAction: "autofill",
        leadResponse: application,
        inquirySelection: {additionalQuestions}
    });
};
